import { Button, PageHeader } from "components";
import { GetServerSidePropsContext, NextPage } from "next";
import { useRouter } from "next/router";
import { NewsletterSection, NewsSection, SingleNewsSection } from "sections";
import {
  IArticleV3Fields,
  IDeliverySectionFields,
  INews,
  IStaticNewsletterSectionFields,
} from "types";
import {
  fetchNews,
  getContentfulEntries,
  toNewsListItem,
} from "utils/contentful-api";

const NewsNotFound = () => {
  const { push } = useRouter();
  return (
    <section className="bg-white">
      <div className="container flex flex-col items-center py-5">
        <p className="text-2xl font-medium">News not found</p>
        <Button
          className="h-[24px] text-[11px] lg:text-[13px] mb-[5px] button-primary mt-4"
          label="Return to news page"
          onClick={() => push("/")}
        />
      </div>
    </section>
  );
};

const News: NextPage<{
  news: IArticleV3Fields | null;
  articles: INews[];
  delivery: IDeliverySectionFields[];
  newsletter: IStaticNewsletterSectionFields[];
}> = ({ news, articles, delivery, newsletter }) => {
  return (
    <div>
      <PageHeader
        ogDescription={news?.shortContent}
        ogImage={`https:${news?.mainImage.fields.file.url}`}
        title={news?.title ?? "News"}
      />
      {news !== null ? (
        <SingleNewsSection delivery={delivery} news={news} />
      ) : (
        <NewsNotFound />
      )}
      <NewsSection news={articles} />
      <NewsletterSection newsletterSection={newsletter} />
    </div>
  );
};
export default News;

export async function getStaticPaths() {
  const articles = await getContentfulEntries<IArticleV3Fields>(
    "articleV3",
    "-sys.createdAt"
  );

  const paths = articles
    .filter((article) => article.slug)
    .map((article) => ({
      params: { newsSlug: article.slug },
    }));

  return {
    paths,
    fallback: "blocking", // Generate pages on-demand for new articles
  };
}

export async function getStaticProps({ params }: GetServerSidePropsContext) {
  if (!params?.newsSlug) {
    return { notFound: true };
  }

  const articleEntries = await getContentfulEntries<IArticleV3Fields>(
    "articleV3",
    "-sys.createdAt",
    8
  );
  const articles = articleEntries.map(toNewsListItem);

  const newsletter = await getContentfulEntries<IStaticNewsletterSectionFields>(
    "newsletterSection"
  );
  const delivery = await getContentfulEntries<IDeliverySectionFields>(
    "deliverySection"
  );

  const news = (await fetchNews(params.newsSlug as string)) ?? null;

  if (!news) {
    return { notFound: true };
  }

  return {
    props: { news, articles, newsletter, delivery },
    revalidate: 3600, // Revalidate every hour
  };
}
